import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Loader2, Plus, Search, Star, X } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PAT } from "@/constants/testIds";
import { searchDiagnosisCodes } from "@/lib/diagnosisApi";
import { catalogItemToDiagnosis } from "@/lib/encounterDiagnoses";
import { PROBLEM_LIST_EMPTY } from "@/lib/patientSummary";
import { formatSearchDiagnosis } from "@/lib/pmhCatalog";
import {
  addProblemListItem,
  hydrateProblemList,
  moveProblemListItem,
  persistProblemList,
  readProblemList,
  removeProblemListItem,
  setPrimaryProblemListItem,
} from "@/lib/diagnosisSources";

const REMOVE_CONFIRM = "Remove this problem from the patient's Problem List?";

function apiErrorMessage(error, fallback = "Unable to search diagnosis codes.") {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((row) => row.msg).join("; ");
  return fallback;
}

export default function ProblemListWorkspace({ patientId, onChange, accessReadOnly = false }) {
  const [items, setItems] = useState(() => readProblemList(patientId));
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setItems(readProblemList(patientId));
    hydrateProblemList(patientId).then((next) => {
      if (!cancelled) setItems(next);
    });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    if (!adding || !debounced) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    searchDiagnosisCodes({ q: debounced, active: true, limit: 25 })
      .then((data) => {
        if (cancelled) return;
        setResults((data.items || []).map(catalogItemToDiagnosis).filter((row) => row.code));
      })
      .catch((error) => {
        if (cancelled) return;
        setResults([]);
        toast.error(apiErrorMessage(error));
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [adding, debounced]);

  async function persist(next) {
    setItems(next);
    onChange?.(next);
    try {
      const saved = await persistProblemList(patientId, next);
      setItems(saved);
      onChange?.(saved);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Unable to save Problem List."));
    }
  }

  function addSelected() {
    if (!selected?.code) return;
    persist(addProblemListItem(patientId, selected));
    setSelected(null);
    toast.success("Added to Problem List");
  }

  function remove(code) {
    if (!window.confirm(REMOVE_CONFIRM)) return;
    persist(removeProblemListItem(patientId, code));
  }

  const selectedCodes = new Set(items.map((row) => row.code));

  return (
    <section
      data-testid={PAT.chartProblemListPanel}
      className="rounded-xl border border-slate-200 bg-[#FFFFFF] p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Problem List</h2>
          <p className="mt-1 text-sm text-slate-500">
            Patient-level Problem List. This is not Medical History and not today&apos;s Encounter
            diagnoses.
          </p>
        </div>
        {accessReadOnly ? null : (
        <Button
          type="button"
          data-testid={PAT.chartProblemAdd}
          onClick={() => {
            setAdding((open) => !open);
            setSelected(null);
            setQuery("");
          }}
          className="h-9 bg-[#1E79E8] hover:bg-[#1869CC]"
        >
          <Plus className="h-4 w-4" />
          Add Problem
        </Button>
        )}
      </div>

      {adding && !accessReadOnly && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[220px] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                type="search"
                autoComplete="off"
                spellCheck={false}
                data-testid={PAT.chartProblemSearch}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setSelected(null);
                }}
                placeholder="Search ICD-10-CM by code or description…"
                className="h-10 bg-white pl-10"
              />
            </div>
            <Button
              type="button"
              data-testid={PAT.chartProblemAddSelected}
              onClick={addSelected}
              disabled={!selected || selectedCodes.has(selected.code)}
              className="h-10 bg-[#1E79E8] hover:bg-[#1869CC]"
            >
              Add
            </Button>
          </div>
          {(searching || results.length > 0) && (
            <ul
              data-testid={PAT.chartProblemResults}
              className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white"
            >
              {searching && (
                <li className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Searching ICD-10-CM…
                </li>
              )}
              {results.map((item) => {
                const active = selected?.code === item.code;
                const added = selectedCodes.has(item.code);
                return (
                  <li key={item.code}>
                    <button
                      type="button"
                      data-testid={`${PAT.chartProblemResult}-${item.code}`}
                      disabled={added}
                      onClick={() => setSelected(item)}
                      className={`block w-full px-3 py-2 text-left text-sm ${
                        added
                          ? "text-slate-400"
                          : active
                            ? "bg-[#E5F0FF] text-[#1E79E8]"
                            : "text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {formatSearchDiagnosis(item)}
                      {added ? " — Added" : ""}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {items.length ? (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table data-testid={PAT.chartProblemTable} className="w-full min-w-[640px] text-sm">
            <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">ICD-10-CM</th>
                <th className="px-3 py-2">Description</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr
                  key={item.code}
                  data-testid={`${PAT.chartProblemRow}-${item.code}`}
                  className="border-t border-slate-100"
                >
                  <td className="px-3 py-2 text-slate-500">{index + 1}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1 font-fhir text-sm font-medium text-[#1E79E8]">
                      {item.isPrimary ? (
                        <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500" />
                      ) : null}
                      {item.code}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-800">
                    <span className="mr-2">{item.description}</span>
                    {item.isPrimary ? (
                      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Primary</Badge>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    {accessReadOnly ? null : (
                    <div className="flex flex-wrap items-center gap-0.5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        disabled={index === 0}
                        aria-label={`Move ${item.code} up`}
                        data-testid={`${PAT.chartProblemMoveUp}-${item.code}`}
                        onClick={() => persist(moveProblemListItem(patientId, item.code, -1))}
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        disabled={index === items.length - 1}
                        aria-label={`Move ${item.code} down`}
                        data-testid={`${PAT.chartProblemMoveDown}-${item.code}`}
                        onClick={() => persist(moveProblemListItem(patientId, item.code, 1))}
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-1.5 text-[11px]"
                        disabled={item.isPrimary}
                        data-testid={`${PAT.chartProblemPrimary}-${item.code}`}
                        onClick={() => persist(setPrimaryProblemListItem(patientId, item.code))}
                      >
                        Make Primary
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-slate-400 hover:text-rose-600"
                        aria-label={`Remove ${item.code}`}
                        data-testid={`${PAT.chartProblemRemove}-${item.code}`}
                        onClick={() => remove(item.code)}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-slate-400">{PROBLEM_LIST_EMPTY}</p>
      )}
    </section>
  );
}

export { REMOVE_CONFIRM };
